/**
 * FIXME: PB-3948, merge with component in common.
 */
import { useState } from 'react';

import { type InputElementProps } from '@admin/molecules/InputElement/InputElement';
import { useTranslation } from '@cms/i18n/client';
import { ProgressBar } from '@common/atoms/ProgressBar';
import { WebpImage } from '@common/atoms/WebpImage';
import { type TmpFile } from '@common/clients/api';
import { logger } from '@common/logger';
import {
    checkFileSize,
    checkMimeType,
    checkMultipleUploads,
    LONG_VIDEO_THRESHOLD,
    MAX_FILESIZE_PER_MINUTE,
    MAX_FILESIZE_PER_MINUTE_LONG,
    MB,
} from '@common/organisms/Form/molecules/FileUploadField/FileUploadField.utils';
import { ThumbnailWidth } from '@common/types/WebpWidth';
import { formatNumber } from '@common/utils/NumbersUtil';

import styles from './FileUpload.module.scss';

export interface Props extends InputElementProps {
    endpoint: (file: File, onProgress?: (progress: number) => void) => Promise<TmpFile>;
    fileType?: 'video' | 'image';
    acceptedMimeTypes?: string[];
}

export const FileUpload = (props: Props) => {
    const [selectedFile, setSelectedFile] = useState<File>();
    const [selectedFileName, setSelectedFileName] = useState<string | undefined>();

    const [selectedVideoDuration, setSelectedVideoDuration] = useState<number | undefined>();
    const [loaded, setLoaded] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const __components = useTranslation('components').t;

    const resetUpload = (event: any) => {
        logger.debug('upload is discarded');
        event.target.value = null; // discard selected file
        setSelectedFile(undefined);
        setErrorMessage('');
    };

    const fileSelectHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setErrorMessage(undefined);
        setSelectedVideoDuration(undefined);

        //validating the type, size amd number of uploaded videos
        if (
            !checkMimeType(event, props, setErrorMessage, __components) ||
            !checkFileSize(event, setErrorMessage, __components) ||
            !checkMultipleUploads(event, setErrorMessage, __components) ||
            !file
        ) {
            // @ts-expect-error should be a string?
            event.target.value = null; // discard selected file
            if (props?.onChange) props.onChange(null);
        } else {
            setSelectedFile(file);
            const fileData = await props.endpoint(file, setLoaded).catch(() => {
                setErrorMessage(__components('FileUpload.uploadError'));
            });

            if (fileData?.filename) {
                setSelectedFileName(fileData.filename);
                if (props.fileType === 'video') {
                    // create the video element but don't add it to the page
                    const vid = document.createElement('video');
                    // create url to use as the src of the video
                    const fileURL = URL.createObjectURL(file);
                    vid.src = fileURL;
                    vid.ondurationchange = function () {
                        logger.debug('Video duration: ', Math.ceil(vid.duration));
                        setSelectedVideoDuration(Math.ceil(vid.duration));
                        vid.remove();
                    };
                }

                if (props?.onChange) props.onChange(fileData);
            }
        }
    };

    // For longer videos we allow a higher bitrate since people often watch this on larger screens
    const maxFileSizePerMinute =
        selectedVideoDuration && selectedVideoDuration > LONG_VIDEO_THRESHOLD
            ? MAX_FILESIZE_PER_MINUTE_LONG
            : MAX_FILESIZE_PER_MINUTE;

    if (
        props.fileType === 'video' &&
        selectedVideoDuration &&
        selectedFile &&
        selectedFile.size &&
        (selectedFile.size / selectedVideoDuration) * 60 > maxFileSizePerMinute
    ) {
        const sizePerMinute = formatNumber((selectedFile.size / MB / selectedVideoDuration) * 60);
        const allowedSizePerMinute = formatNumber(maxFileSizePerMinute / MB);
        return (
            <div className={[styles.FileUpload, styles['error']].join(' ')}>
                <button className={styles['delete-button']} onClick={resetUpload}>
                    X
                </button>
                <span>
                    <b>File: </b>
                    {selectedFile.name.length <= 50
                        ? selectedFile.name
                        : selectedFile.name.slice(0, 60) + '....'}
                </span>
                <br />
                <span>
                    {__components('FileUpload.sizePerMinuteError', { sizePerMinute, allowedSizePerMinute })}
                </span>
            </div>
        );
    } else if (props.value && props.fileType === 'image' && !selectedFile) {
        return (
            <div style={{ display: 'flex' }}>
                <WebpImage
                    src={(props.value as string) || ''}
                    alt={''}
                    defaultSize={ThumbnailWidth.WIDTH_48}
                    width={ThumbnailWidth.WIDTH_48}
                    height={ThumbnailWidth.WIDTH_48}
                />
                <input
                    type="file"
                    name="file"
                    onChange={fileSelectHandler}
                    accept={props.acceptedMimeTypes ? props.acceptedMimeTypes.join(', ') : undefined}
                />
            </div>
        );
    } else if (!selectedFile) {
        return (
            <>
                <input
                    type="file"
                    name="file"
                    onChange={fileSelectHandler}
                    accept={props.acceptedMimeTypes ? props.acceptedMimeTypes.join(', ') : undefined}
                />
                {errorMessage ? <p style={{ color: 'red' }}>{errorMessage}</p> : ''}
                <input name={props.id} type="hidden" value="" />
            </>
        );
    } else if (!selectedFileName) {
        return (
            <div
                className={[
                    styles.FileUpload,
                    loaded < 100 ? styles.uploading : styles['done-uploading'],
                ].join(' ')}
            >
                <span>
                    <b>File: </b>
                    {selectedFile.name.length <= 50
                        ? selectedFile.name
                        : selectedFile.name.slice(0, 60) + '....'}
                </span>

                <span>
                    <ProgressBar percentage={Number(loaded.toFixed(0))} />
                    <button className={styles['delete-button']} onClick={resetUpload}>
                        X
                    </button>
                </span>
            </div>
        );
    } else {
        return (
            <div
                className={[
                    styles.FileUpload,
                    loaded < 100 ? styles.uploading : styles['done-uploading'],
                ].join(' ')}
                data-testid="file-upload-complete"
            >
                <button className={styles['delete-button']} onClick={resetUpload}>
                    X
                </button>
                <span>
                    <b>File: </b>
                    {selectedFile.name.length <= 50
                        ? selectedFile.name
                        : selectedFile.name.slice(0, 60) + '....'}
                </span>
                <input
                    name={props.id + '.filename'}
                    type="hidden"
                    value={selectedFileName}
                    accept={props.acceptedMimeTypes ? props.acceptedMimeTypes.join(', ') : undefined}
                />
                {selectedVideoDuration ? (
                    <input name={props.id + '.duration'} type="hidden" value={selectedVideoDuration} />
                ) : (
                    ''
                )}
            </div>
        );
    }
};
